import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useEntitlements } from './hooks/useEntitlements';
import { ThemeProvider } from './contexts/ThemeContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import LoadingSpinner from './components/ui/LoadingSpinner';
import ErrorBoundary from './components/ui/ErrorBoundary';
import FeedbackWidget from './components/feedback/FeedbackWidget';

// Auth pages
import SignInPage from './pages/auth/SignInPage';
import SignUpPage from './pages/auth/SignUpPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import UnlockAccountPage from './pages/auth/UnlockAccountPage';
import MfaResetPage from './pages/auth/MfaResetPage';
import MfaResetRequestPage from './pages/auth/MfaResetRequestPage';
import InviteAcceptPage from './pages/auth/InviteAcceptPage';
import OAuthConsentPage from './pages/oauth/OAuthConsentPage';
import { consumeIntendedRoute, peekIntendedRoute } from './utils/intendedRoute';
import { initSessionLifecycle } from './services/session';

// Layout & pages
import DataSourcesPage from './pages/sources/DataSourcesPage';
import SourceOverviewPage from './pages/sources/SourceOverviewPage';
import SettingsPage from './pages/settings/SettingsPage';
import ProfilePage from './pages/settings/ProfilePage';
import WorkspacePage from './pages/settings/WorkspacePage';
import CreateWorkspacePage from './pages/onboarding/CreateWorkspacePage';
import { NotificationChannelsPage } from './pages/settings/NotificationChannelsPage';
import BillingPage from './pages/settings/BillingPage';
import AccountClosurePage from './pages/settings/AccountClosurePage';
import TeamPage from './pages/settings/TeamPage';
import SessionsPage from './pages/settings/SessionsPage';
import ExternalAiAccessPage from './pages/settings/ExternalAiAccessPage';


// Analysis pages removed - v2_analysis functionality no longer available

// Dynamic Analysis pages
import DynamicAnalysisPage from './pages/dynamic-analysis/DynamicAnalysisPage';
import DynamicJobDetailPage from './pages/dynamic-analysis/JobDetailPage';
import ConfigureExecutionJobPage from './pages/dynamic-analysis/ConfigureExecutionJobPage';
import ResultDetailPage from './pages/dynamic-analysis/ResultDetailPage';

// Account integration pages
import IntegrationsPage from './pages/accounts/IntegrationsPage';
import RelationshipsPage from './pages/accounts/RelationshipsPage';
import QuickBooksSuccessPage from './pages/accounts/QuickBooksSuccessPage';
import XeroSuccessPage from './pages/accounts/XeroSuccessPage';
import LightspeedConnectPage from './pages/accounts/LightspeedConnectPage';
import PlaidSuccessPage from './pages/accounts/PlaidSuccessPage';
import GoogleDriveSuccessPage from './pages/accounts/GoogleDriveSuccessPage';
import WebhookSetup from './pages/integrations/WebhookSetup';
import InboundFileSetup from './pages/integrations/InboundFileSetup';
import WebhookEditConfig from './pages/integrations/WebhookEditConfig';


// Chat Pages
import { ChatThreadPage, ChatHome, ChatHistoryPage } from './pages/chat';
import ChatLayout from './components/layout/ChatLayout';
import HomeRollup from './pages/home/HomeRollup';
import ConnectAiPage from './pages/connect-ai/ConnectAiPage';
import QueryLogPage from './pages/query-log/QueryLogPage';
import AdminUsersPage from './pages/platform-admin/AdminUsersPage';
import ImpersonationBanner from './components/layout/ImpersonationBanner';
import McpQuotaBanner from './components/layout/McpQuotaBanner';
import SemanticsPage from './pages/semantics/SemanticsPage';
import DataViewerPage from './pages/data-viewer/DataViewerPage';
import BusinessesPage from './pages/businesses/BusinessesPage';
import WikiPage from './pages/wiki/WikiPage';

// Dashboard Builder Pages
import { NewDashboardPage, DashboardBuildPage, DashboardHomePage, DashboardViewPage, DashboardCastPage, SharedDashboardPage } from './pages/dashboard-builder';

// Routines Pages — saved prompts that an agent runs on a schedule
import {
  RoutinesPage,
  CreateRoutinePage,
  RoutineDetailPage,
} from './pages/routines';

// Pulse Pages — automated transaction monitoring
import {
  PulseAccountsPage,
  PulseFeedPage,
  PulseActivatePage,
  PulseAccountPage,
} from './pages/pulse';


// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, isOrphan } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Orphans (authenticated, no active membership anywhere) have no workspace to
  // render — route them to the create-workspace screen, and keep everyone else off it.
  if (isOrphan && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />;
  }
  if (!isOrphan && location.pathname === '/welcome') {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// Restricts a route to internal testers (users.is_internal_tester) while a
// feature is pre-GA. Authenticated non-testers are bounced to /home. The API is
// gated too (server-side), so this is a UX guard, not the security boundary.
const InternalTesterRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isInternalTester) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// Restricts a route to Zamino staff (users.platform_role). Deliberately does NOT
// use ProtectedRoute: the portal is cross-tenant, so an orphaned staff account
// with no workspace must still reach it. The API is gated on platform_role too,
// so this is a UX guard, not the security boundary.
const PlatformAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, isPlatformAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// Internal-tester pages migrated off the old sidebar/header layout onto ChatLayout.
// They were authored to sit inside that layout's outer content padding, which
// ChatLayout does not supply, so we re-add it once here instead of editing every
// render branch across each page.
const InternalPaddedPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <InternalTesterRoute>
    <ChatLayout>
      <div className="p-4 sm:p-6 lg:p-8">{children}</div>
    </ChatLayout>
  </InternalTesterRoute>
);

// Restricts a route to workspaces entitled to a tier feature (e.g. Pulse is
// Scale-only). Rendered only once authenticated (so the entitlements query is
// authorized); non-entitled users are bounced to /home. The API is entitlement-
// gated server-side too, so this is a UX guard, not the security boundary.
const FeatureGate: React.FC<{ feature: 'pulse'; children: React.ReactNode }> = ({ feature, children }) => {
  const { data, isLoading } = useEntitlements();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!data?.features?.[feature]) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

const FeatureRoute: React.FC<{ feature: 'pulse'; children: React.ReactNode }> = ({ feature, children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <FeatureGate feature={feature}>{children}</FeatureGate>;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// Component to handle route restoration from SPA fallback and auth events
const RouteRestorer: React.FC = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if there's an intended route stored from the 404 fallback
    const intendedRoute = consumeIntendedRoute();
    if (intendedRoute) {
      // Navigate to the intended route immediately
      navigate(intendedRoute, { replace: true });
    }
  }, [navigate]);
  
  useEffect(() => {
    // Handle unauthorized events from axios interceptor
    const handleUnauthorized = () => {
      navigate('/login', { replace: true });
    };
    
    // Handle logout events
    const handleLogout = () => {
      navigate('/login', { replace: true });
    };
    
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:logout', handleLogout);
    
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [navigate]);
  
  return null; // This component doesn't render anything
};

// Root redirect component that checks for intended routes
const RootRedirect: React.FC = () => {
  const intendedRoute = peekIntendedRoute();
  
  // If there's an intended route, show loading while RouteRestorer handles navigation
  if (intendedRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  // Otherwise, redirect to the home roll-up
  return <Navigate to="/home" replace />;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public auth routes — Cool Linen redesign (Phase 2 of sign-up redesign) */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <SignInPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignUpPage />
          </PublicRoute>
        }
      />
      {/* Legacy URL — keep responding to /register so old links / bookmarks land somewhere */}
      <Route path="/register" element={<Navigate to="/signup" replace />} />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/unlock-account"
        element={<UnlockAccountPage />}
      />
      <Route
        path="/mfa-reset"
        element={<MfaResetPage />}
      />
      <Route
        path="/mfa-reset-request"
        element={
          <PublicRoute>
            <MfaResetRequestPage />
          </PublicRoute>
        }
      />

      {/* Shared Dashboard — public, no auth required */}
      <Route
        path="/d/:shareToken"
        element={<SharedDashboardPage />}
      />

      {/* Invitation acceptance — public, no auth wrapper */}
      <Route
        path="/invite/:token"
        element={<InviteAcceptPage />}
      />

      {/* OAuth consent (self-hosted AS) — handles its own auth: unauthenticated
          users bounce to /login with this URL stashed for post-login return */}
      <Route path="/oauth/consent" element={<OAuthConsentPage />} />

      {/* Protected Routes */}
      <Route
        path="/sources"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <DataSourcesPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sources/integrations"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <IntegrationsPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      {/* Per-source overview. Declared after /sources/integrations so the static
          segment wins over the :sourceId param. */}
      <Route
        path="/sources/:sourceId"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <SourceOverviewPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      {/* /accounts* → /sources* (route rename, D11/D51) */}
      <Route path="/accounts" element={<Navigate to="/sources" replace />} />
      <Route path="/accounts/integrations" element={<Navigate to="/sources/integrations" replace />} />
      <Route path="/accounts/connect" element={<Navigate to="/sources/integrations" replace />} />
      <Route path="/accounts/relationships" element={<Navigate to="/sources/relationships" replace />} />
      <Route
        path="/integrations/webhook/setup"
        element={
          <ProtectedRoute>
            <WebhookSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations/webhook/:dataSourceId/edit"
        element={
          <ProtectedRoute>
            <WebhookEditConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations/inbound-file/setup"
        element={
          <ProtectedRoute>
            <InboundFileSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations/inbound-file/:dataSourceId/edit"
        element={
          <ProtectedRoute>
            <InboundFileSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sources/relationships"
        element={
          <InternalPaddedPage>
            <RelationshipsPage />
          </InternalPaddedPage>
        }
      />
      <Route
        path="/integrations/quickbooks/success"
        element={<QuickBooksSuccessPage />}
      />
      <Route
        path="/integrations/xero/success"
        element={<XeroSuccessPage />}
      />
      <Route
        path="/integrations/lightspeed/connect"
        element={<LightspeedConnectPage />}
      />
      <Route
        path="/integrations/google-drive/success"
        element={<GoogleDriveSuccessPage />}
      />
      <Route
        path="/integrations/plaid/success"
        element={
          <ProtectedRoute>
            <PlaidSuccessPage />
          </ProtectedRoute>
        }
      />
      {/* Dynamic Analysis Routes — internal testers only */}
      <Route
        path="/dynamic-analysis"
        element={
          <InternalPaddedPage>
            <DynamicAnalysisPage />
          </InternalPaddedPage>
        }
      />
      <Route
        path="/dynamic-analysis/jobs/:jobId"
        element={
          <InternalPaddedPage>
            <DynamicJobDetailPage />
          </InternalPaddedPage>
        }
      />
      <Route
        path="/dynamic-analysis/jobs/:jobId/execution"
        element={
          <InternalPaddedPage>
            <ConfigureExecutionJobPage />
          </InternalPaddedPage>
        }
      />

      <Route
        path="/dynamic-analysis/results/:resultId"
        element={
          <InternalPaddedPage>
            <ResultDetailPage />
          </InternalPaddedPage>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <SettingsPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/profile"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <ProfilePage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/workspace"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <WorkspacePage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <NotificationChannelsPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <BillingPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/team"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <TeamPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/sessions"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <SessionsPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/account-closure"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <AccountClosurePage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      {/* Security merged into Profile; keep the old path working for bookmarks */}
      <Route path="/settings/security" element={<Navigate to="/settings/profile" replace />} />
      <Route
        path="/settings/external-ai-access"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <ExternalAiAccessPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />

      {/* Orphan first-run — create your first/only workspace (no ChatLayout: there's
          no workspace context to render the sidebar/pill). ProtectedRoute routes
          orphans here and everyone else away. */}
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <CreateWorkspacePage />
          </ProtectedRoute>
        }
      />

      {/* Home — workspace roll-up (all businesses) */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <HomeRollup />
            </ChatLayout>
          </ProtectedRoute>
        }
      />

      {/* Guided MCP setup — connect ChatGPT/Claude/etc. to Zamino */}
      <Route
        path="/connect-ai"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <ConnectAiPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />

      {/* Data-platform pages (Phase 1a placeholders — replaced phase by phase) */}
      <Route
        path="/businesses"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <BusinessesPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-viewer"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <DataViewerPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/semantics"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <SemanticsPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/semantics/:entityId"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <SemanticsPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/query-log"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <QueryLogPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      {/* Zamino staff portal — cross-tenant, gated on users.platform_role */}
      <Route
        path="/admin/users"
        element={
          <PlatformAdminRoute>
            <ChatLayout>
              <AdminUsersPage />
            </ChatLayout>
          </PlatformAdminRoute>
        }
      />
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      {/* Wiki (Phase 6, D27) — reached via Businesses/sources, not in the nav */}
      <Route
        path="/wiki"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <WikiPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wiki/:pageId"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <WikiPage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />

      {/* AI Chat Pages — internal testers only while pre-GA. Everyone else is
          bounced to /home. This is a UX guard; the API is the security boundary. */}
      <Route
        path="/chat"
        element={
          <InternalTesterRoute>
            <ChatLayout>
              <ChatHome />
            </ChatLayout>
          </InternalTesterRoute>
        }
      />
      <Route
        path="/chat/history"
        element={
          <InternalTesterRoute>
            <ChatLayout>
              <ChatHistoryPage />
            </ChatLayout>
          </InternalTesterRoute>
        }
      />
      <Route
        path="/chat/:threadId"
        element={
          <InternalTesterRoute>
            <ChatLayout>
              <ChatThreadPage />
            </ChatLayout>
          </InternalTesterRoute>
        }
      />

      {/* Dashboard Builder Pages */}
      <Route
        path="/dashboards"
        element={
          <ProtectedRoute>
            <ChatLayout>
              <DashboardHomePage />
            </ChatLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboards/new"
        element={
          <ProtectedRoute>
            <NewDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboards/:id/build"
        element={
          <ProtectedRoute>
            <DashboardBuildPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboards/:id/cast"
        element={
          <ProtectedRoute>
            <DashboardCastPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboards/:id"
        element={
          <ProtectedRoute>
            <DashboardViewPage />
          </ProtectedRoute>
        }
      />

      {/* Routines — internal testers only while pre-GA */}
      <Route
        path="/routines"
        element={
          <InternalTesterRoute>
            <ChatLayout>
              <RoutinesPage />
            </ChatLayout>
          </InternalTesterRoute>
        }
      />
      <Route
        path="/routines/create"
        element={
          <InternalTesterRoute>
            <ChatLayout>
              <CreateRoutinePage />
            </ChatLayout>
          </InternalTesterRoute>
        }
      />
      <Route
        path="/routines/:routineId"
        element={
          <InternalTesterRoute>
            <ChatLayout>
              <RoutineDetailPage />
            </ChatLayout>
          </InternalTesterRoute>
        }
      />
      <Route
        path="/routines/:routineId/edit"
        element={
          <InternalTesterRoute>
            <ChatLayout>
              <RoutineDetailPage />
            </ChatLayout>
          </InternalTesterRoute>
        }
      />

      {/* Pulse — Scale-only (entitlement-gated) automated transaction monitoring */}
      <Route
        path="/pulse"
        element={
          <FeatureRoute feature="pulse">
            <ChatLayout>
              <PulseAccountsPage />
            </ChatLayout>
          </FeatureRoute>
        }
      />
      <Route
        path="/pulse/feed"
        element={
          <FeatureRoute feature="pulse">
            <ChatLayout>
              <PulseFeedPage />
            </ChatLayout>
          </FeatureRoute>
        }
      />
      <Route
        path="/pulse/accounts/:accountId"
        element={
          <FeatureRoute feature="pulse">
            <ChatLayout>
              <PulseAccountPage />
            </ChatLayout>
          </FeatureRoute>
        }
      />
      <Route
        path="/pulse/accounts/:accountId/activate"
        element={
          <FeatureRoute feature="pulse">
            <ChatLayout>
              <PulseActivatePage />
            </ChatLayout>
          </FeatureRoute>
        }
      />

      {/* Legacy redirects from the old CDO scheduler URLs */}
      <Route path="/cdo" element={<Navigate to="/routines" replace />} />
      <Route path="/cdo/jobs/create" element={<Navigate to="/routines/create" replace />} />
      <Route path="/cdo/jobs/:jobId" element={<Navigate to="/routines" replace />} />
      <Route path="/cdo/jobs/:jobId/edit" element={<Navigate to="/routines" replace />} />

      {/* Legacy redirect — /dashboard was removed */}
      <Route path="/dashboard" element={<Navigate to="/home" replace />} />

      {/* Default redirects */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  // Renews the access token ahead of expiry and whenever the tab regains focus,
  // so a tab left open overnight is fresh before its first query fires.
  useEffect(() => initSessionLifecycle(), []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <TimezoneProvider>
            <Router future={{ 
              v7_startTransition: true,
              v7_relativeSplatPath: true 
            }}>
              <div className="app">
                <RouteRestorer />
                {/* Above the routes so no page layout can hide or scroll it away. */}
                <ImpersonationBanner />
                <McpQuotaBanner />
                <AppRoutes />

                {/* Floating "share feedback" widget (authenticated users only) */}
                <FeedbackWidget />

                {/* Toast Notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: 'var(--background)',
                      color: 'var(--foreground)',
                      border: '1px solid var(--border)',
                    },
                    success: {
                      iconTheme: {
                        primary: 'rgb(34 197 94)',
                        secondary: 'white',
                      },
                    },
                    error: {
                      iconTheme: {
                        primary: 'rgb(239 68 68)',
                        secondary: 'white',
                      },
                    },
                  }}
                />
              </div>
            </Router>
            </TimezoneProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;